import {
  Checkbox,
  ClearButtonBehaviour,
  ColorInput,
  GroupBox,
  Labeled,
  Theme,
} from '@infotech-soe/erp-ui-kit';
import React, { CSSProperties, memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'react-jss';
import { validateHTMLColor } from 'validate-color';

import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { getPresetColors } from '../../../../../store/selectors/userConfig';
import userConfig from '../../../../../store/slices/userConfig';
import { applyOddEvenRowStyles } from '../../utils/applyOddEvenRowStyles';
import { isEven } from '../../utils/isEven';
import { getCellFontStyles } from '../../utils/stylingHandler';

import { messages } from './messages';
import {
  ColorInputName,
  InputConditionsProps,
  InputName,
  TColorInputName,
} from './types';

interface Props extends Pick<
  InputConditionsProps<boolean>,
  'condition' | 'onChange'
> {
  onColorChange: (newValue: string | null, ruleName: TColorInputName) => void;
}

const CellFormatter = ({
  condition: { color, value },
  onChange,
  onColorChange,
}: Props): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const theme = useTheme<Theme>();
  const dispatch = useAppDispatch();

  const userPresetColors = useAppSelector(getPresetColors);

  const handleClosePreset = useCallback(
    (presetColors: string[]) => {
      dispatch(userConfig.saveGeneral({ config: { presetColors } }));
    },
    [dispatch]
  );

  const colorErrText = useMemo(
    () =>
      color.backgroundColor &&
      color.color &&
      color.backgroundColor === color.color
        ? `: ${formatMessage(messages.textColorErr)}`
        : '',
    [color.backgroundColor, color.color, formatMessage]
  );

  const exampleStyles = useCallback(
    (rowIndex: number): CSSProperties => ({
      ...getCellFontStyles(value),
      backgroundColor: validateHTMLColor(color.backgroundColor)
        ? applyOddEvenRowStyles(color, rowIndex).backgroundColor
        : isEven(rowIndex)
          ? theme.colors.backgroundGridPrimary
          : theme.colors.backgroundGroup,

      color: validateHTMLColor(color.color)
        ? color.color
        : theme.colors.typoCommon,
      padding: 5,
    }),
    [color, theme, value]
  );

  return (
    <>
      <div className="row col-12">
        <GroupBox
          className="col-7 m-mt-md m-pa-none"
          label={formatMessage(messages.style) + colorErrText}
        >
          <Labeled label={formatMessage(messages.textColor)}>
            <ColorInput
              name={ColorInputName.COLOR}
              value={color.color}
              onChange={(v: string | null) =>
                onColorChange(v, ColorInputName.COLOR)
              }
              onClosePreset={handleClosePreset}
              userPresetColors={userPresetColors}
              withClearButton={ClearButtonBehaviour.BOTH}
            />
          </Labeled>
          <Labeled label={formatMessage(messages.backgroundColor)}>
            <ColorInput
              name={ColorInputName.BACKGROUNDCOLOR}
              value={color.backgroundColor}
              onChange={(v: string | null) =>
                onColorChange(v, ColorInputName.BACKGROUNDCOLOR)
              }
              onClosePreset={handleClosePreset}
              userPresetColors={userPresetColors}
              withClearButton={ClearButtonBehaviour.BOTH}
            />
          </Labeled>
        </GroupBox>
        <GroupBox
          className="col-5 m-mt-md"
          label={formatMessage(messages.font) + colorErrText}
        >
          <GroupBox.Content className="column items-start">
            <Checkbox
              className="m-my-md"
              label={formatMessage(messages.textBold)}
              name={InputName.TEXTBOLD}
              value={value.textBold}
              onChange={onChange}
            />
            <Checkbox
              className="m-mb-md"
              label={formatMessage(messages.textItalic)}
              name={InputName.TEXTITALIC}
              value={value.textItalic}
              onChange={onChange}
            />
            <Checkbox
              className="m-mb-md"
              label={formatMessage(messages.textUnderline)}
              name={InputName.TEXTUNDERLINE}
              value={value.textUnderline}
              onChange={onChange}
            />
            <Checkbox
              label={formatMessage(messages.textLineThrough)}
              name={InputName.TEXTLINETHROUGH}
              value={value.textLineThrough}
              onChange={onChange}
            />
          </GroupBox.Content>
        </GroupBox>
      </div>
      <div style={{ border: `1px solid ${theme.colors.borderControlStatic}` }}>
        <div style={exampleStyles(1)}>
          {formatMessage(messages.example, { parity: 'odd' })}
        </div>
        <div style={exampleStyles(2)}>
          {formatMessage(messages.example, { parity: 'even' })}
        </div>
      </div>
    </>
  );
};

export default memo(CellFormatter);
